"use client"

import { Box, Flex, Text, Icon, Skeleton, SimpleGrid } from "@chakra-ui/react"
import { FaArrowUp, FaArrowDown } from "react-icons/fa"

import { neutral, signal, type, radius, elevation, numeric } from "../../../theme/tokens"
import { fmtR, fmtPct } from "../format"

// One metric — the Sales page's tile, unchanged in spirit: eyebrow, number, one line of
// context, colour only on the change indicator.
const Kpi = ({ label, value, sub, delta, tone, isLoading, isLast }) => {
  if (isLoading) {
    return (
      <Box px={4} py={3.5} borderRight={isLast ? "none" : "1px solid"} borderColor={neutral[200]}>
        <Skeleton h="9px" w="60%" mb={2.5} />
        <Skeleton h="22px" w="75%" mb={2} />
        <Skeleton h="9px" w="45%" />
      </Box>
    )
  }
  const hasDelta = delta !== undefined && delta !== null
  const deltaTone = hasDelta && Number(delta) >= 0 ? signal.up : signal.down
  return (
    <Box
      px={4}
      py={3.5}
      minW={0}
      borderRight={{ base: "none", md: isLast ? "none" : "1px solid" }}
      borderBottom={{ base: isLast ? "none" : "1px solid", md: "none" }}
      borderColor={neutral[200]}
    >
      <Text
        fontSize={type.size.micro}
        fontWeight={type.weight.semibold}
        letterSpacing={type.tracking.label}
        textTransform="uppercase"
        color={neutral[500]}
        noOfLines={1}
      >
        {label}
      </Text>
      <Flex align="baseline" gap={2} mt={1.5}>
        <Text
          fontSize={type.size.display}
          fontWeight={type.weight.semibold}
          letterSpacing={type.tracking.display}
          lineHeight={1.05}
          color={tone || neutral[900]}
          sx={numeric}
          noOfLines={1}
        >
          {value}
        </Text>
        {hasDelta && (
          <Flex align="center" gap="3px" flexShrink={0}>
            <Icon as={Number(delta) >= 0 ? FaArrowUp : FaArrowDown} boxSize="9px" color={deltaTone} />
            <Text fontSize={type.size.caption} fontWeight={type.weight.semibold} color={deltaTone} sx={numeric}>
              {fmtPct(delta)}
            </Text>
          </Flex>
        )}
      </Flex>
      {sub && (
        <Text fontSize={type.size.caption} color={neutral[400]} mt={1} sx={numeric} noOfLines={1}>
          {sub}
        </Text>
      )}
    </Box>
  )
}

/**
 * The strip above the tables: the selection's two month pairs, its store coverage and
 * the basis of every number on the page.
 *   measures = { cur_ly, cur_ty, cur_yoy, prev_ly, prev_ty, prev_yoy, stores, storesWithSales }
 */
const RepKpis = ({ measures, period, rep, isLoading }) => {
  const m = measures || {}
  const L = period?.labels || {}
  const tiles = [
    {
      label: `${L.cur || "Current month"} · MTD`,
      value: fmtR(m.cur_ty),
      sub: `${L.curLY || "Last year"} ${fmtR(m.cur_ly)}`,
      delta: m.cur_yoy,
    },
    {
      label: L.prev || "Previous month",
      value: fmtR(m.prev_ty),
      sub: `${L.prevLY || "Last year"} ${fmtR(m.prev_ly)}`,
      delta: m.prev_yoy,
    },
    {
      // Names whose numbers these are: the selected rep's, or every rep's.
      label: rep ? `Stores · ${rep}` : "Stores · all reps",
      value: m.stores != null ? `${m.storesWithSales ?? 0} / ${m.stores}` : "–",
      sub: "with sales / assigned",
    },
    {
      label: "Basis",
      value: "Sales at cost",
      sub: period?.latestDay ? `data to ${period.latestDay}${period.imported ? ` · imported ${period.imported}` : ""}` : "",
    },
  ]
  return (
    <Box bg={neutral[0]} border="1px solid" borderColor={neutral[200]} borderRadius={radius.md} boxShadow={elevation.raised} mb={4} overflow="hidden">
      <SimpleGrid columns={{ base: 1, md: 4 }}>
        {tiles.map((t, i) => (
          <Kpi key={t.label} {...t} isLoading={isLoading} isLast={i === tiles.length - 1} />
        ))}
      </SimpleGrid>
    </Box>
  )
}

export default RepKpis
